import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Consumer, ConsumerStatus, ResumeStrategy } from '@/api/bol-notifications';
import { PrimaryButton, SecondaryButton } from '@/components';
import { ConsumerFormFields, ConsumerFormData } from './ConsumerFormFields';

export interface ConsumerEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ConsumerFormData & {
    status?: ConsumerStatus;
    resumeStrategy?: ResumeStrategy;
    resumeTimestamp?: Date | null;
  }) => Promise<void>;
  consumer: Consumer;
}

interface FormData extends ConsumerFormData {
  status: ConsumerStatus;
  resumeStrategy?: ResumeStrategy;
  resumeTimestamp?: Date | null;
}

export const ConsumerEditDialog: React.FC<ConsumerEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  consumer,
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      name: '',
      status: ConsumerStatus.Active,
      resumeStrategy: undefined,
      resumeTimestamp: null,
      webhook: {
        url: '',
        method: 'POST',
        headers: [],
        queryParameters: [],
      },
      batchCapacity: 10,
      batchMinInterval: 1000,
    }
  });

  const { isSubmitting } = form.formState;
  const watchedResumeStrategy = useWatch({
    control: form.control,
    name: 'resumeStrategy',
  });

  const watchedStatus = useWatch({
    control: form.control,
    name: 'status',
  });

  useEffect(() => {
    if (watchedStatus === ConsumerStatus.Disabled) {
      form.setValue('resumeStrategy', undefined);
      form.setValue('resumeTimestamp', null);
    }
  }, [watchedStatus, form]);

  const { fields: headerFields, append: appendHeader, remove: removeHeader } = useFieldArray({
    control: form.control,
    name: 'webhook.headers',
  });

  const { fields: queryParamFields, append: appendQueryParam, remove: removeQueryParam } = useFieldArray({
    control: form.control,
    name: 'webhook.queryParameters',
  });

  useEffect(() => {
    if (open && consumer) {
      form.setValue('name', consumer.name);
      form.setValue('status', consumer.status || ConsumerStatus.Active);
      form.setValue('resumeStrategy', undefined);
      form.setValue('resumeTimestamp', null);
      form.setValue('webhook.url', consumer.webhook?.url || '');
      form.setValue('webhook.method', consumer.webhook?.method || 'POST');
      form.setValue('webhook.headers', consumer.webhook?.headers || []);
      form.setValue('webhook.queryParameters', consumer.webhook?._queryParameters || []);
      form.setValue('batchCapacity', consumer.batch?.capacity || 10);
      form.setValue('batchMinInterval', consumer.batch?.minInterval || 1000);
    }
  }, [open, consumer, form]);

  const onFormSubmit = async (data: FormData) => {
    try {
      await onSubmit(data);
      form.reset();
      onClose();
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Consumer</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={form.handleSubmit(onFormSubmit)}>
          {/* Display read-only info when editing */}
          {consumer && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Consumer ID</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{consumer.id}</Typography>
            </Box>
          )}

          <ConsumerFormFields
            control={form.control}
            errors={form.formState.errors}
            headerFields={headerFields}
            appendHeader={appendHeader}
            removeHeader={removeHeader}
            queryParamFields={queryParamFields}
            appendQueryParam={appendQueryParam}
            removeQueryParam={removeQueryParam}
          />

          <Divider sx={{ my: 2 }} />

          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  <MenuItem value={ConsumerStatus.Active}>Active</MenuItem>
                  <MenuItem value={ConsumerStatus.Disabled}>Disabled</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {/* Resume options for adjusting consumption offset - only show when status is ACTIVE */}
          {watchedStatus === ConsumerStatus.Active && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                Resume Strategy (Optional)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Use these options to adjust the notification consumption offset. This is useful when re-enabling a disabled consumer or to replay/skip messages.
              </Alert>

              <Controller
                name="resumeStrategy"
                control={form.control}
                render={({ field }) => (
                  <FormControl fullWidth margin="dense" sx={{ mb: 1 }}>
                    <InputLabel>Resume Strategy</InputLabel>
                    <Select {...field} label="Resume Strategy" value={field.value || ''}>
                      <MenuItem value="">
                        <em>No change (keep current offset)</em>
                      </MenuItem>
                      <MenuItem value={ResumeStrategy.Continue}>Continue - Resume from last committed offset</MenuItem>
                      <MenuItem value={ResumeStrategy.Latest}>Latest - Skip pending messages, start from now</MenuItem>
                      <MenuItem value={ResumeStrategy.Earliest}>Earliest - Resume from beginning of retention period</MenuItem>
                      <MenuItem value={ResumeStrategy.FromTimestamp}>From Timestamp - Resume from a specific time</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              {watchedResumeStrategy === ResumeStrategy.FromTimestamp && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="resumeTimestamp"
                    control={form.control}
                    rules={{
                      required: watchedResumeStrategy === ResumeStrategy.FromTimestamp ? 'Timestamp is required for FROM_TIMESTAMP strategy' : false
                    }}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Resume From Timestamp *"
                        value={field.value || null}
                        onChange={(newValue) => field.onChange(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            margin: 'dense',
                            error: !!form.formState.errors.resumeTimestamp,
                            helperText: form.formState.errors.resumeTimestamp?.message || "Select the timestamp from which to resume consumption",
                            sx: { mb: 1 }
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton onClick={form.handleSubmit(onFormSubmit)} type="submit" disabled={isSubmitting}>
          Update
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
